import { useTranslation } from "react-i18next";

export default function AboutSection() {
  const { t } = useTranslation();

  const missions = [
    {
      icon: "ri-store-2-line",
      title: t("about.mission1_title"),
      desc: t("about.mission1_desc"),
    },
    {
      icon: "ri-motorbike-line",
      title: t("about.mission2_title"),
      desc: t("about.mission2_desc"),
    },
    {
      icon: "ri-restaurant-2-line",
      title: t("about.mission3_title"),
      desc: t("about.mission3_desc"),
    },
    {
      icon: "ri-hand-heart-line",
      title: t("about.mission4_title"),
      desc: t("about.mission4_desc"),
    },
  ];

  return (
    <section className="relative w-full py-20 md:py-28 bg-background-50">
      <div className="w-full px-4 md:px-6 lg:px-10 max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-14 md:mb-20">
          <span className="inline-block px-4 py-1.5 rounded-full bg-secondary-100 text-secondary-800 text-xs font-semibold uppercase tracking-wider mb-5">
            {t("about.title")}
          </span>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 mb-5">
            {t("about.title")}
          </h2>
          <p className="text-base md:text-lg text-foreground-600 max-w-xl mx-auto">
            {t("about.subtitle")}
          </p>
          <div className="w-20 h-1 bg-primary-500 rounded-full mx-auto mt-5"></div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center mb-16 md:mb-20">
          {/* Images collage */}
          <div className="relative">
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-2xl overflow-hidden h-40 md:h-48">
                <img
                  src="https://readdy.ai/api/search-image?query=Cameroonian%20street%20food%20vendor%20woman%20smiling%20grilling%20fish%20over%20charcoal%20open%20flame%20colorful%20market%20background%20warm%20golden%20light%20authentic%20candid%20moment%20editorial%20photography&width=500&height=500&seq=about-collage-1-v2&orientation=squarish"
                  alt="Street food vendor"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="rounded-2xl overflow-hidden h-40 md:h-48 mt-6">
                <img
                  src="https://readdy.ai/api/search-image?query=Cameroonian%20motorcycle%20taxi%20rider%20in%20Douala%20urban%20street%20colorful%20buildings%20background%20warm%20afternoon%20light%20authentic%20local%20transportation%20scene%20editorial%20photography&width=500&height=500&seq=about-collage-2-v2&orientation=squarish"
                  alt="Motorcycle rider"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="rounded-2xl overflow-hidden h-40 md:h-48">
                <img
                  src="https://readdy.ai/api/search-image?query=Traditional%20Cameroonian%20dishes%20assortment%20ndole%20poulet%20DG%20plantains%20achu%20yellow%20soup%20served%20in%20bowls%20on%20colorful%20African%20fabric%20tablecloth%20warm%20lighting%20appetizing%20editorial%20food%20photography&width=500&height=500&seq=about-collage-3-v2&orientation=squarish"
                  alt="Cameroonian dishes"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="rounded-2xl overflow-hidden h-40 md:h-48 mt-6">
                <img
                  src="https://readdy.ai/api/search-image?query=Colorful%20Cameroonian%20open%20air%20market%20Douala%20fresh%20produce%20piles%20spices%20textiles%20vibrant%20atmosphere%20warm%20sunshine%20natural%20colors%20bustling%20scene%20editorial%20photography&width=500&height=500&seq=about-collage-4-v2&orientation=squarish"
                  alt="Local market"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            {/* Decorative element */}
            <div className="absolute -bottom-5 -right-5 w-28 h-28 bg-accent-100/60 rounded-full -z-10"></div>
            <div className="absolute -top-5 -left-5 w-20 h-20 bg-primary-100/60 rounded-full -z-10"></div>
          </div>

          {/* Text Content */}
          <div className="space-y-6">
            <p className="text-base md:text-lg text-foreground-700 leading-relaxed">
              {t("about.description1")}
            </p>
            <p className="text-base md:text-lg text-foreground-700 leading-relaxed">
              {t("about.description2")}
            </p>
            <p className="text-base md:text-lg text-foreground-700 leading-relaxed">
              {t("about.description3")}
            </p>
            <div className="pt-4">
              <div className="inline-flex items-center gap-2 px-5 py-3 bg-primary-100/60 rounded-full">
                <div className="w-8 h-8 rounded-full bg-primary-500 flex items-center justify-center">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-double-quotes-l text-background-50 text-xs"></i>
                  </div>
                </div>
                <span className="text-sm font-medium text-primary-800 italic">
                  {t("footer.tagline")}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Mission Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {missions.map((mission, index) => (
            <div
              key={index}
              className="bg-background-100 rounded-2xl p-6 md:p-7 border border-background-200 hover:shadow-md transition-all duration-300 hover:border-primary-200 group"
            >
              <div className="w-11 h-11 rounded-xl bg-primary-100 flex items-center justify-center mb-4 group-hover:bg-primary-200 transition-colors">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className={`${mission.icon} text-primary-600 text-lg`}></i>
                </div>
              </div>
              <h3 className="font-heading text-sm font-semibold text-foreground-950 mb-2">
                {mission.title}
              </h3>
              <p className="text-sm text-foreground-600 leading-relaxed">
                {mission.desc}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}